import { LiveToolbar } from "@faire/prototype-playground/platform/components/toolbar/LiveToolbar";
import {
  PlaygroundLocalMsg,
  usePlaygroundLocalMsg,
} from "@faire/prototype-playground/platform/localization";
import { ChevronLeft } from "@faire/slate-icons/ChevronLeft";
import { HorizontalEllipsis } from "@faire/slate-icons/HorizontalEllipsis";
import { IconButton } from "@faire/slate/components/icon-button";
import { tsv } from "@faire/slate/lib/tsv";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import RetailerLayout from "../../../components/RetailerLayout";
import { CompassProvider } from "../../../contexts/CompassContext";
import type { FaireProductEntity } from "@faire/playground-entity-db/entity_types/product";
import { getEntityByToken } from "../../../data/get_entity";

import {
  BRAND_FRAMING_DIVIDERS,
  brandFramingForVariant,
  brandFramingVariantGroup,
} from "./brandFramingVariants";
import {
  BrandGroupedProducts,
  savedProductGridStyle,
} from "./BrandGroupedProducts";
import {
  CART_ACTION_PILL,
  cartActionForVariant,
  cartActionVariantGroup,
} from "./cartActionVariants";
import { getMoreIdeasProducts } from "./moreIdeas";
import { MoreIdeasFab } from "./MoreIdeasFab";
import { MoreIdeasSection } from "./MoreIdeasSection";
import { CART_ROUTE, HOME_ROUTE, SAVED_PRODUCTS_ROUTE } from "./routes";
import {
  ALL_FAVORITES_COLLECTION_ID,
  useCollections,
  useProductTokensByCollection,
} from "./savedStore";
import { SavedProductsSideCart } from "./SavedProductsSideCart";
import { SavedProductTile } from "./SavedProductTile";
import { useAccountDropdown } from "./useAccountDropdown";
import { useBrandCart } from "./useBrandCart";
import {
  SAVE_FLOW_POPOVER,
  useFavoritesController,
} from "./useFavoritesController";
import { type SavedViewMode, ViewModeToggle } from "./ViewModeToggle";

/**
 * Collection page (Figma 9687:321032): the collection name with the
 * Products / Brands switch and an overflow action, then the collection's
 * products, grouped under brand lockups in the Brands view, with the brand
 * side cart beside the content and "More ideas" underneath.
 */
export default function SavedProductsCollectionDetailPage() {
  return (
    <CompassProvider>
      <CollectionDetailPageInner />
    </CompassProvider>
  );
}

const CollectionDetailPageInner = () => {
  const history = useHistory();
  const { collectionId } = useParams<{ collectionId: string }>();
  const favorites = useFavoritesController({
    saveFlowVariant: SAVE_FLOW_POPOVER,
  });
  const accountDropdown = useAccountDropdown();
  const collections = useCollections();
  const productTokensByCollection = useProductTokensByCollection();
  const [viewMode, setViewMode] = useState<SavedViewMode>("products");
  const [cartActionVariant, setCartActionVariant] =
    useState<number>(CART_ACTION_PILL);
  const cartAction = cartActionForVariant(cartActionVariant);
  const [brandFramingVariant, setBrandFramingVariant] = useState<number>(
    BRAND_FRAMING_DIVIDERS
  );
  const brandFraming = brandFramingForVariant(brandFramingVariant);
  const moreOptionsLabel = usePlaygroundLocalMsg("More options");

  const collection = collections.find((entry) => entry.id === collectionId);
  const collectionName = collection?.name ?? "Collection";

  useEffect(() => {
    document.title = `Saved Products & Cart — ${collectionName}`;
  }, [collectionName]);

  const tokens = productTokensByCollection[collectionId] ?? [];
  const products = tokens
    .map((productToken) => getEntityByToken(productToken, "product"))
    .filter((product): product is FaireProductEntity => Boolean(product));
  const moreIdeas = getMoreIdeasProducts(
    new Set(productTokensByCollection[ALL_FAVORITES_COLLECTION_ID] ?? [])
  );
  const cart = useBrandCart(products, {
    openOnQuantityChange: viewMode === "brands",
  });

  // The side cart belongs to the brand view; leaving it closes the cart.
  const handleViewModeChange = (nextViewMode: SavedViewMode) => {
    setViewMode(nextViewMode);
    if (nextViewMode === "products") {
      cart.closeSideCart();
    }
  };

  const renderTile = (product: FaireProductEntity) => (
    <SavedProductTile
      key={product.token}
      product={product}
      favorites={favorites}
      cart={cart}
    />
  );

  return (
    <>
      <LiveToolbar
        variantGroups={[
          cartActionVariantGroup(cartActionVariant, setCartActionVariant),
          brandFramingVariantGroup(brandFramingVariant, setBrandFramingVariant),
        ]}
      />
      <RetailerLayout
        logoLinkUrl={HOME_ROUTE}
        cartHref={CART_ROUTE}
        onCartClick={() => history.push(CART_ROUTE)}
        cartCount={favorites.savedCount}
        accountDropdown={accountDropdown}
      >
        <div className="flex w-full items-start">
          <div className={pageStyle.cls()}>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div className="flex flex-col gap-1">
                <button
                  type="button"
                  onClick={() => history.push(SAVED_PRODUCTS_ROUTE)}
                  className="text-fs-text-primary hover:text-fs-text-subdued -ml-1 flex items-center gap-0.5 self-start transition-colors"
                >
                  <ChevronLeft width="16" height="16" />
                  <span className="type-fs-label-medium">
                    <PlaygroundLocalMsg defaultMessage="Saved products" />
                  </span>
                </button>
                <h1 className="type-fs-page-heading text-fs-text-primary">
                  {collectionName}
                </h1>
              </div>
              <div className="flex items-center gap-3">
                <ViewModeToggle
                  value={viewMode}
                  onChange={handleViewModeChange}
                />
                <IconButton
                  aria-label={moreOptionsLabel}
                  Icon={HorizontalEllipsis}
                  variant="secondary"
                  size="small"
                  shape="circle"
                />
              </div>
            </div>

            {products.length === 0 ? (
              <div className="flex justify-center py-16">
                <p className="type-fs-paragraph text-fs-text-subdued">
                  <PlaygroundLocalMsg defaultMessage="This collection is empty." />
                </p>
              </div>
            ) : viewMode === "brands" ? (
              <BrandGroupedProducts
                products={products}
                cart={cart}
                cartAction={cartAction}
                framing={brandFraming}
                renderTile={renderTile}
              />
            ) : (
              <div
                className={savedProductGridStyle.cls({
                  compact: cart.isSideCartOpen,
                })}
              >
                {products.map(renderTile)}
              </div>
            )}

            <div className="mt-6">
              <MoreIdeasSection
                products={moreIdeas}
                renderTile={renderTile}
                compact={cart.isSideCartOpen}
              />
            </div>
          </div>
          {cart.sideCartBrand ? (
            <SavedProductsSideCart
              open={cart.isSideCartOpen}
              brandName={cart.sideCartBrand.name}
              minimum={cart.sideCartBrand.minimum}
              items={cart.sideCartItems}
              onChangeQuantity={cart.setCartQuantity}
              onRemove={(productToken) => cart.setCartQuantity(productToken, 0)}
              onViewCart={() => history.push(CART_ROUTE)}
              onClose={cart.closeSideCart}
            />
          ) : null}
        </div>
      </RetailerLayout>
      <MoreIdeasFab
        imageUrls={moreIdeas.slice(0, 2).map((product) => product.imageUrl)}
      />
      {favorites.popoverElement}
      {favorites.toastElement}
      {favorites.collectionToastElement}
    </>
  );
};

const pageStyle = tsv({
  base: [
    "mx-auto flex w-full min-w-0 max-w-[1920px] flex-1 flex-col gap-6",
    "px-6 pt-8 pb-16 lg:px-12",
  ],
});
